import React, { useState, useEffect } from 'react';
import config from '@/config/environment';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

interface AuthGateProps {
  children: React.ReactNode;
}

interface UserInfo {
  gamertag: string;
  xid: string;
  uhs: string;
  displayName: string;
  ageGroup: string;
}

interface ValidationResponse {
  valid: boolean;
  user?: UserInfo;
  sessionToken?: string;
  tokenExpiry?: string;
}

const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasValidated, setHasValidated] = useState(false);

  useEffect(() => {
    // If tokens exist in localStorage, fast-path auth without re-validating
    const cachedSession = localStorage.getItem('xbox_session_token');
    const cachedUser = localStorage.getItem('xbox_user_info');
    const tokenExpiry = localStorage.getItem('xbox_token_expiry');
    const urlParams = new URLSearchParams(window.location.search);
    const hasAccessParam = urlParams.has('access');
    const hasXblParam = urlParams.has('xbl'); // DEV: raw XBL3.0 token

    if (!hasValidated) {
      setHasValidated(true);
      // If xbl param is present, always re-validate (dev mode token refresh)
      if (hasXblParam) {
        validateAccess();
        return;
      }
      if (!hasAccessParam && cachedSession && cachedUser) {
        try {
          const parsed = JSON.parse(cachedUser) as UserInfo;
          // Optional expiry check
          if (tokenExpiry) {
            const exp = Date.parse(tokenExpiry);
            if (!isNaN(exp) && Date.now() > exp) {
              // expired; require revalidation if access provided
              validateAccess();
              return;
            }
          }
          setIsAuthenticated(true);
          setUserInfo(parsed);
          setIsValidating(false);
          return;
        } catch {}
      }
      validateAccess();
    }
  }, [hasValidated]);

  const validateAccess = async () => {
    try {
      setIsValidating(true);
      setError(null);

      // Get access token from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const accessToken = urlParams.get('access');
      
      // DEV ONLY: Allow passing raw XBL3.0 token directly for local testing
      // Usage: ?xbl=XBL3.0 x=userhash;token
      const rawXblToken = urlParams.get('xbl');
      if (rawXblToken && config.isDevelopment) {
        console.log('[DEV] Using raw XBL3.0 token bypass');
        // Store the raw token for API calls
        localStorage.setItem('xbox_xbl_token', rawXblToken);
        localStorage.setItem('xbox_session_token', 'dev-session');
        
        // Clean URL first
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('xbl');
        window.history.replaceState({}, document.title, newUrl.toString());
        
        // Fetch actual profile via backend proxy (avoids CORS)
        try {
          console.log('[DEV] Fetching profile via backend...');
          const profileRes = await fetch(`${config.apiUrl}/xbl/profile/me`, {
            method: 'GET',
            headers: {
              'Authorization': rawXblToken,
              'Content-Type': 'application/json',
            },
          });
          
          if (profileRes.ok) {
            const profileData = await profileRes.json();
            const user = profileData.profileUsers?.[0];
            const settings = user?.settings || [];
            const getSetting = (id: string) => settings.find((s: any) => s.id === id)?.value || '';
            
            // Extract userhash from the token (format: XBL3.0 x=userhash;token)
            const xuidMatch = rawXblToken.match(/x=([^;]+)/);
            const uhs = xuidMatch?.[1] || '';
            
            const actualUserInfo: UserInfo = {
              gamertag: getSetting('Gamertag') || getSetting('GameDisplayName') || getSetting('AppDisplayName') || 'Unknown',
              xid: user?.id || '',
              uhs: uhs,
              displayName: getSetting('GameDisplayName') || getSetting('Gamertag') || 'Unknown',
              ageGroup: 'Adult',
            };
            
            console.log('[DEV] Profile fetched:', actualUserInfo.gamertag, 'XUID:', actualUserInfo.xid);
            localStorage.setItem('xbox_user_info', JSON.stringify(actualUserInfo));
            setIsAuthenticated(true);
            setUserInfo(actualUserInfo);
            setIsValidating(false);
            return;
          } else {
            const errText = await profileRes.text();
            console.error('[DEV] Profile fetch failed:', profileRes.status, errText);
          }
        } catch (err) {
          console.error('[DEV] Profile fetch error:', err);
        }
        
        // Fallback if profile fetch fails
        const fallbackUser: UserInfo = {
          gamertag: 'DevUser (profile fetch failed)',
          xid: 'dev-xuid',
          uhs: 'dev-uhs',
          displayName: 'Development User',
          ageGroup: 'Adult',
        };
        localStorage.setItem('xbox_user_info', JSON.stringify(fallbackUser));
        setIsAuthenticated(true);
        setUserInfo(fallbackUser);
        setIsValidating(false);
        return;
      }

      console.log('Access token from URL:', accessToken ? 'Present (length: ' + accessToken.length + ')' : 'Missing');

      if (!accessToken) {
        setError('No access token provided. Please use a valid access link.');
        setIsValidating(false);
        return;
      }

      console.log('Making validation request to backend...');

      // Validate with backend
      const response = await fetch(`${config.apiUrl}/validate-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ access: accessToken }),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      const data: (ValidationResponse & { xboxToken?: string }) | { error: string } = await response.json();
      console.log('Response data:', data);

      if (!response.ok || 'error' in data) {
        const errorMsg = 'error' in data ? data.error : 'Access validation failed';
        console.error('Validation failed:', errorMsg);
        setError(errorMsg);
        setIsValidating(false);
        return;
      }

      if ((data as any).valid && (data as any).user && (data as any).sessionToken) {
        const d = data as ValidationResponse & { xboxToken?: string };
        console.log('Validation successful for user:', d.user!.gamertag);
        setIsAuthenticated(true);
        setUserInfo(d.user!);
        
        // Store session token for API requests
        localStorage.setItem('xbox_session_token', d.sessionToken!);
        localStorage.setItem('xbox_user_info', JSON.stringify(d.user));
        // Cache encrypted access for worker auth and invalidate caches when it changes
        const newAccess = accessToken || '';
        const prevAccess = localStorage.getItem('xbox_encrypted_access') || '';
        if (newAccess && newAccess !== prevAccess) {
          try {
            localStorage.removeItem('friends_cache');
          } catch {}
        }
        if (newAccess) localStorage.setItem('xbox_encrypted_access', newAccess);
        if ((d as any).tokenExpiry) {
          localStorage.setItem('xbox_token_expiry', (d as any).tokenExpiry as any);
        }
        
        // Clean URL by removing access parameter
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('access');
        window.history.replaceState({}, document.title, newUrl.toString());
      } else {
        setError('Invalid access token');
      }

    } catch (error) {
      console.error('Access validation error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleRetry = () => {
    validateAccess();
  };

  if (isValidating) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
            <CardTitle>Validating Access</CardTitle>
            <CardDescription>
              Please wait while we verify your Xbox authentication...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md mx-4">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle className="text-destructive">Access Denied</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={handleRetry} variant="outline" className="mr-2">
              Retry
            </Button>
            <Button 
              onClick={() => window.location.href = window.location.origin}
              variant="secondary"
            >
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isAuthenticated && userInfo) {
    return (
      <div className="min-h-screen bg-background">
        {/* Success notification */}
        <div className="bg-green-500/10 border-b border-green-500/20 p-3">
          <div className="mx-auto flex max-w-[1440px] flex-col gap-2 px-1 sm:flex-row sm:items-center sm:justify-between sm:px-4">
            <div className="flex min-w-0 items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="min-w-0 truncate text-sm text-green-500">
                Authenticated as <strong>{userInfo.gamertag}</strong>
              </span>
            </div>
            <div className="hidden items-center space-x-2 text-xs text-green-500/80 sm:flex">
              <Shield className="h-4 w-4" />
              <span>Secure Session Active</span>
            </div>
          </div>
        </div>
        
        {/* Main application content */}
        {children}
      </div>
    );
  }

  // Fallback - should not reach here
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Shield className="h-12 w-12 text-muted-foreground" />
          </div>
          <CardTitle>Access Required</CardTitle>
          <CardDescription>
            Please use a valid access link to view this application.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
};

export default AuthGate;
